import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, ViewChild } from '@angular/core';
import { BentoAlertItemOptions, BentoContextualHeaderItem } from '@bento/bento-ng';
import { Location } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';
import { MigrationService } from '../migration-service/migration.service';
import { HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/internal/operators/catchError';
import { of } from 'rxjs/internal/observable/of';
import { TranslateService } from '@ngx-translate/core';
import { getCompanyId } from '@onviobr/services/session';
import * as wjcCore from '@grapecity/wijmo';
import * as wjcGrid from '@grapecity/wijmo.grid';
import { DateTimeService } from '../migration-date-time/date-time.service';

@Component({
	selector: 'app-migration-client-registration',
	templateUrl: './migration-client-registration.component.html',
	styleUrls: ['./migration-client-registration.component.css'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MigrationClientRegistrationComponent implements OnInit {

	@ViewChild('flexGrid') flexGrid: wjcGrid.FlexGrid;
	@ViewChild('toolbar', {static: true}) toolbar;
	alerts: BentoAlertItemOptions[] = [];
	showBusyloader: boolean;
	fullToken = {
		authSchema: 'UDSLongToken',
		token: this.cookieService.get('UDSLongToken')
	};
	tab = 'notSent';
	companyId: string;
	clientRegStatusesUrl = '/api/profiles/v2/companies/';
	sendRegistrationUrl = '/api/profiles/v1/registration';
	coreContactUrl = '/api/core/v7/companies/';
	columnDefinitions: any[] = [
		{
			binding: 'code',
			header: 'ID',
			visible: true,
			width: '*'
		},
		{
			binding: 'displayAs',
			header: 'Name',
			visible: true,
			width: '*'
		},
		{
			binding: 'userType',
			header: 'Type',
			visible: true,
			width: '*'
		},
		{
			binding: 'loginEmail',
			header: 'Email',
			visible: true,
			width: '*'
		}
	];
	toolbarData: any[] = [
		{
			label: 'Send registration emails',
			icon: 'bento-icon-envelope',
			static: true,
			hidden: false,
			disabled: true,
			class: 'send-registration-emails',
			action: () => {
				this.sendRegistration();
			}
		}
	];
	contacts: wjcCore.CollectionView;
	notSentContacts: wjcCore.CollectionView;
	pendingContacts: wjcCore.CollectionView;
	selectedEntries: any = [];
	successPosts: any = [];
	failedPosts: any = [];
	headerSelected = false;
	// MULTI-SELECT Checkbox
	CHECKBOX_INDETERMINATE = 0;
	CHECKBOX_UNSELECTED = 1;
	CHECKBOX_SELECTED = 2;
	headerCheckBoxMode = this.CHECKBOX_UNSELECTED;
	formatted: any;
	notSentCount: any;
	pendingCount: any;
	public getCompanyId = getCompanyId;
	headerItems: BentoContextualHeaderItem[];

	constructor(
		private cookieService: CookieService,
		public migrationService: MigrationService,
		private changeDetectorRef: ChangeDetectorRef,
		private translate: TranslateService,
		public dateTimeService: DateTimeService,
		private location: Location
	) {
		this.headerItems = [
			new BentoContextualHeaderItem('Client Center Registrations', null),
		];
	}

	async ngOnInit() {
		this.companyId = await this.getCompanyId();
		this.getContactsList();
	}

	getContactsList() {
		this.showBusyloader = true;
		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
			Authorization: this.fullToken.authSchema + ' ' + this.fullToken.token
		});
		// Fetch the NOT SENT (UNREGISTERED) Contacts
		this.migrationService
			.get(
				this.clientRegStatusesUrl + this.companyId + '/contacts?registrationStatus=UNREGISTERED&licenseType=CLIENT_CENTER', headers
			)
			.pipe(
				catchError(err => {
					const errorMessage = this.translate.instant('onvio.setup.migrations.netfirm.http.error',
						{ action: 'Client registration status', error: err });
					this.showAlert(errorMessage);
					this.showBusyloader = false;
					this.changeDetectorRef.markForCheck();
					return of([]);
				})).subscribe((response) => {
				if (response) {
					const contacts = this.showGivenAndLastName(response.items);
					this.notSentContacts = new wjcCore.CollectionView(contacts);
					this.notSentCount = this.notSentContacts.itemCount;
					this.navigateTab(this.tab);
					this.changeDetectorRef.markForCheck();
				}
				this.showBusyloader = false;
			});
		// Fetch the PENDING Contacts
		this.migrationService
			.get(this.clientRegStatusesUrl + this.companyId + '/contacts?registrationStatus=PENDING&licenseType=CLIENT_CENTER', headers)
			.pipe(
				catchError(err => {
					const errorMessage = this.translate.instant('onvio.setup.migrations.netfirm.http.error',
						{ action: 'Client registration status', error: err });
					this.showAlert(errorMessage);
					this.showBusyloader = false;
					this.changeDetectorRef.markForCheck();
					return of([]);
				})).subscribe((response) => {
				if (response) {
					const items = response.items;
					items.forEach(row => {
						row.registrationSent = this.formatDateTime(row.registrationSent);
					});
					const contacts = this.showGivenAndLastName(items);
					this.pendingContacts = new wjcCore.CollectionView(contacts);
					this.pendingCount = this.pendingContacts.itemCount;
					this.navigateTab(this.tab);
					this.changeDetectorRef.markForCheck();
				}
			});
	}

	navigateTab(tab) {
		this.tab = tab;
		if (tab === 'pending') {
			this.toolbarData[0].label = 'Resend registration emails';
			this.contacts = this.pendingContacts;
		} else {
			this.toolbarData[0].label = 'Send registration emails';
			this.contacts = this.notSentContacts;
		}
		this.flexGrid.refresh();
		this.toolbar.refresh();
		this.changeDetectorRef.markForCheck();
	}

	showGivenAndLastName(items) {
		const contacts = [];
		items.forEach(item => {
			if (item.displayAs === null) {
				item.displayAs = item.givenName + ' ' + item.familyName;
			}
			contacts.push(item);
		});
		this.changeDetectorRef.markForCheck();
		return contacts;
	}

	sendRegistration() {
		this.showBusyloader = true;
		this.successPosts = [];
		this.failedPosts = [];
		for (let i = 0; i < this.selectedEntries.length; i++) {
			const payload = {
				companyId: this.companyId,
				contactId: this.selectedEntries[i].contactId,
				email: this.selectedEntries[i].loginEmail
			};
			this.postRegistrationEmail(payload).then(
				() => {
					// Success
					this.changeDetectorRef.markForCheck();
					if (i === this.selectedEntries.length - 1) {
						this.alerts = [{
							type: 'success',
							msg: 'Registration email successfully sent.',
							timeout: 10000,
							closeable: true
						}];
						this.resetGrid();
						this.toggleToolbarButtons();
						this.getContactsList();
					}
				},
				() => {
					// Error
					this.changeDetectorRef.markForCheck();
					this.showBusyloader = false;
					this.alerts = [{
						type: 'danger',
						msg: 'Unable to send the registration email. Please try again.',
						timeout: 10000,
						closeable: true
					}];
				}
			);
		}
	}

	postRegistrationEmail(payload) {
		let headers = new HttpHeaders({
			'Content-Type': 'application/json'
		});
		let patchData = [];
		const promise = new Promise((resolve, reject) => {
			// Send registration email
			this.migrationService.post(this.sendRegistrationUrl, payload, headers).subscribe(() => {
				this.successPosts.push(payload.email);
				headers = new HttpHeaders({
					'Content-Type': 'application/json',
					Authorization: this.fullToken.authSchema + ' ' + this.fullToken.token
				});
				patchData = [
					{
						op: 'replace',
						path: '/userIdRequestDateTime',
						value: this.getISOTimestamp()
					}
				];
				// Update Core field userIdRequestDateTime
				this.migrationService.patch(this.coreContactUrl + this.companyId + '/contacts/' + payload.contactId, patchData)
					.subscribe(() => {
						resolve(undefined);
					});
			},
			() => {
				this.failedPosts.push(payload.loginEmail);
				reject();
			}
			);
		});
		return promise;
	}

	getISOTimestamp() {
		return new Date().toISOString();
	}

	formatDateTime(date) {
		const formattedDateTime = this.dateTimeService.getDateTimeFromJSDate(new Date(date));
		return formattedDateTime;
	}

	resetGrid() {
		this.headerCheckBoxMode = this.CHECKBOX_UNSELECTED;
		this.headerSelected = false;
		this.selectedEntries = [];
	}

	toggleToolbarButtons() {
		if (this.toolbarData[0].disabled) {
			this.toolbarData[0].disabled = false;
		} else {
			this.toolbarData[0].disabled = true;
		}
		this.toolbar.refresh();
	}

	onHeaderCheckboxChange() {
		this.selectedEntries = [];
		let selected = false;
		if (this.headerCheckBoxMode !== this.CHECKBOX_SELECTED) {
			this.headerCheckBoxMode = this.CHECKBOX_SELECTED;
			this.headerSelected = true;
			this.toolbarData[0].disabled = false;
			selected = true;
		} else {
			this.headerCheckBoxMode = this.CHECKBOX_UNSELECTED;
			this.headerSelected = false;
			this.toolbarData[0].disabled = true;
			selected = false;
		}
		for (let i = 0, ttl = this.flexGrid.rows.length; i < ttl; i++) {
			// logic for disabled checkbox
			this.flexGrid.rows[i].dataItem.selected = selected;
			if (selected) {
				this.selectedEntries.push(this.flexGrid.rows[i].dataItem);
			} else {
				this.selectedEntries = [];
			}
		}
		this.flexGrid.refresh();
		this.toolbar.refresh();
	}

	onCheckBoxChange(item) {
		// Pushes/removes selected/deselected items in array
		if (item.selected === true) {
			this.selectedEntries.push(item);
		} else {
			const index = this.selectedEntries.indexOf(item);
			if (index !== -1) {
				this.selectedEntries.splice(index, 1);
			}
		}

		this.headerCheckBoxMode = this.CHECKBOX_UNSELECTED;
		this.headerSelected = false;
		let count = 0;
		// Count how many checkboxes are selected
		for (let i = 0, ttl = this.flexGrid.rows.length; i < ttl; i++) {
			if (this.flexGrid.rows[i].dataItem.selected === true) {
				count++;
			}
		}

		this.checkboxWrapUpSelect(count, this.flexGrid.rows.length);
		this.flexGrid.refresh();
		this.toolbar.refresh();
	}

	checkboxWrapUpSelect(count, flexGridRowLength) {
		// If all are selected, mark the header checkbox
		if (count === flexGridRowLength) {
			this.headerCheckBoxMode = this.CHECKBOX_SELECTED;
			this.headerSelected = true;
			this.toolbarData[0].disabled = false;
		} else if (count > 0) {
			// If only some checkboxes are marked, set header checkbox to indeterminate
			this.headerCheckBoxMode = this.CHECKBOX_INDETERMINATE;
			this.headerSelected = false;
			this.toolbarData[0].disabled = false;
		} else if (count === 0) {
			// If no checkboxes are marked, toggle the checkbox
			// Values for header checkbox are already set to false / unselected
			this.toolbarData[0].disabled = true;
		}
	}

	showAlert(message: string, type: string = 'danger') {
		this.alerts.push({
			type,
			msg: message,
			closeable: true
		});
	}

	back() {
		sessionStorage.setItem('backFromClientCenter', 'ok');
		this.location.back();
	}

}
